/**
 * ISO 3166-1 alpha-2 country codes for the client "País" select.
 *
 * We store only the 2-letter code (matches `addressSchema.countryCode`) and render
 * the display name with `Intl.DisplayNames` in the active UI language — so the same
 * list localizes to Spanish or English without a hardcoded names table.
 */
export const COUNTRY_CODES: readonly string[] = [
  'AF','AL','DZ','AD','AO','AG','AR','AM','AU','AT','AZ','BS','BH','BD','BB','BY',
  'BE','BZ','BJ','BT','BO','BA','BW','BR','BN','BG','BF','BI','KH','CM','CA','CV',
  'CF','TD','CL','CN','CO','KM','CG','CD','CR','CI','HR','CU','CY','CZ','DK','DJ',
  'DM','DO','EC','EG','SV','GQ','ER','EE','SZ','ET','FJ','FI','FR','GA','GM','GE',
  'DE','GH','GR','GD','GT','GN','GW','GY','HT','HN','HU','IS','IN','ID','IR','IQ',
  'IE','IL','IT','JM','JP','JO','KZ','KE','KI','KW','KG','LA','LV','LB','LS','LR',
  'LY','LI','LT','LU','MG','MW','MY','MV','ML','MT','MH','MR','MU','MX','FM','MD',
  'MC','MN','ME','MA','MZ','MM','NA','NR','NP','NL','NZ','NI','NE','NG','MK','NO',
  'OM','PK','PW','PA','PG','PY','PE','PH','PL','PT','QA','RO','RU','RW','KN','LC',
  'VC','WS','SM','ST','SA','SN','RS','SC','SL','SG','SK','SI','SB','SO','ZA','KR',
  'SS','ES','IC','LK','SD','SR','SE','CH','SY','TW','TJ','TZ','TH','TL','TG','TO','TT',
  'TN','TR','TM','TV','UG','UA','AE','GB','US','UY','UZ','VU','VA','VE','VN','YE',
  'ZM','ZW',
];

/** Default country (Jalupro is a Spanish company). */
export const DEFAULT_COUNTRY_CODE = 'ES';

/** Flag emoji for an ISO-2 country code (regional-indicator pair); '' if malformed. */
export function countryFlag(code: string): string {
  const cc = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(
    ...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65),
  );
}

/** Localized country name for an ISO-2 code; falls back to the code itself. */
export function countryName(code: string, lang: string): string {
  const cc = code.toUpperCase();
  try {
    const dn = new Intl.DisplayNames([lang], { type: 'region' });
    // Canary Islands (reserved code IC) are listed under Spain so they sort next to it.
    if (cc === 'IC') return `${dn.of('ES') ?? 'Spain'} – ${dn.of('IC') ?? 'Canary Islands'}`;
    return dn.of(cc) ?? code;
  } catch {
    return code;
  }
}

/** Country list as { code, name }, sorted by localized name for the current language. */
export function sortedCountries(lang: string): ReadonlyArray<{ code: string; name: string }> {
  return COUNTRY_CODES.map((code) => ({ code, name: countryName(code, lang) })).sort((a, b) =>
    a.name.localeCompare(b.name, lang),
  );
}

/**
 * International dial codes (ITU E.164 country calling codes) for the phone input's
 * country selector. Keyed by the same ISO-2 codes as COUNTRY_CODES.
 */
export const DIAL_CODES: Readonly<Record<string, string>> = {
  AF: '+93', AL: '+355', DZ: '+213', AD: '+376', AO: '+244', AG: '+1268', AR: '+54',
  AM: '+374', AU: '+61', AT: '+43', AZ: '+994', BS: '+1242', BH: '+973', BD: '+880',
  BB: '+1246', BY: '+375', BE: '+32', BZ: '+501', BJ: '+229', BT: '+975', BO: '+591',
  BA: '+387', BW: '+267', BR: '+55', BN: '+673', BG: '+359', BF: '+226', BI: '+257',
  KH: '+855', CM: '+237', CA: '+1', CV: '+238', CF: '+236', TD: '+235', CL: '+56',
  CN: '+86', CO: '+57', KM: '+269', CG: '+242', CD: '+243', CR: '+506', CI: '+225',
  HR: '+385', CU: '+53', CY: '+357', CZ: '+420', DK: '+45', DJ: '+253', DM: '+1767',
  DO: '+1809', EC: '+593', EG: '+20', SV: '+503', GQ: '+240', ER: '+291', EE: '+372',
  SZ: '+268', ET: '+251', FJ: '+679', FI: '+358', FR: '+33', GA: '+241', GM: '+220',
  GE: '+995', DE: '+49', GH: '+233', GR: '+30', GD: '+1473', GT: '+502', GN: '+224',
  GW: '+245', GY: '+592', HT: '+509', HN: '+504', HU: '+36', IS: '+354', IN: '+91',
  ID: '+62', IR: '+98', IQ: '+964', IE: '+353', IL: '+972', IT: '+39', JM: '+1876',
  JP: '+81', JO: '+962', KZ: '+7', KE: '+254', KI: '+686', KW: '+965', KG: '+996',
  LA: '+856', LV: '+371', LB: '+961', LS: '+266', LR: '+231', LY: '+218', LI: '+423',
  LT: '+370', LU: '+352', MG: '+261', MW: '+265', MY: '+60', MV: '+960', ML: '+223',
  MT: '+356', MH: '+692', MR: '+222', MU: '+230', MX: '+52', FM: '+691', MD: '+373',
  MC: '+377', MN: '+976', ME: '+382', MA: '+212', MZ: '+258', MM: '+95', NA: '+264',
  NR: '+674', NP: '+977', NL: '+31', NZ: '+64', NI: '+505', NE: '+227', NG: '+234',
  MK: '+389', NO: '+47', OM: '+968', PK: '+92', PW: '+680', PA: '+507', PG: '+675',
  PY: '+595', PE: '+51', PH: '+63', PL: '+48', PT: '+351', QA: '+974', RO: '+40',
  RU: '+7', RW: '+250', KN: '+1869', LC: '+1758', VC: '+1784', WS: '+685', SM: '+378',
  ST: '+239', SA: '+966', SN: '+221', RS: '+381', SC: '+248', SL: '+232', SG: '+65',
  SK: '+421', SI: '+386', SB: '+677', SO: '+252', ZA: '+27', KR: '+82', SS: '+211',
  ES: '+34', IC: '+34', LK: '+94', SD: '+249', SR: '+597', SE: '+46', CH: '+41', SY: '+963',
  TW: '+886', TJ: '+992', TZ: '+255', TH: '+66', TL: '+670', TG: '+228', TO: '+676',
  TT: '+1868', TN: '+216', TR: '+90', TM: '+993', TV: '+688', UG: '+256', UA: '+380',
  AE: '+971', GB: '+44', US: '+1', UY: '+598', UZ: '+998', VU: '+678', VA: '+39',
  VE: '+58', VN: '+84', YE: '+967', ZM: '+260', ZW: '+263',
};

/** Preferred ISO code for dial prefixes shared by several countries. */
const DIAL_PREFERRED: Readonly<Record<string, string>> = {
  '+1': 'US',
  '+7': 'RU',
  '+39': 'IT',
  '+44': 'GB',
};

/**
 * Split a stored phone string ("+34 600111222") into { countryCode, number } for the
 * phone input. Matches the LONGEST dial-code prefix; unknown/unprefixed phones land in
 * `number` as-is with the default (ES) country.
 */
export function splitPhone(phone: string): { countryCode: string; number: string } {
  const raw = phone.trim();
  if (!raw.startsWith('+')) {
    return { countryCode: DEFAULT_COUNTRY_CODE, number: raw };
  }
  const compact = raw.replace(/[\s()\-.]/g, '');
  let best: { code: string; dial: string } | null = null;
  for (const [code, dial] of Object.entries(DIAL_CODES)) {
    if (compact.startsWith(dial) && (!best || dial.length > best.dial.length)) {
      best = { code, dial };
    }
  }
  if (!best) return { countryCode: DEFAULT_COUNTRY_CODE, number: raw };
  const code = DIAL_PREFERRED[best.dial] ?? best.code;
  return { countryCode: code, number: compact.slice(best.dial.length) };
}
